import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Search, FileText, Code2, TestTube, TrendingUp } from 'lucide-react';

const steps = [
  {
    icon: Search,
    number: "01",
    title: "Diagnoza",
    desc: "Quiz kwalifikacyjny + 20-min rozmowa. Określamy gdzie tracisz pieniądze i co automatyzować w pierwszej kolejności.",
    detail: "~20 min",
  },
  {
    icon: FileText,
    number: "02",
    title: "Plan + cennik",
    desc: "Dostajesz konkretny plan działania, harmonogram i fixed-price wycenę. Zero ukrytych kosztów.",
    detail: "2-3 dni",
  },
  {
    icon: Code2,
    number: "03",
    title: "Build",
    desc: "Buduję system w 2-4 tygodniach. Update co kilka dni przez Discord, dostajesz dostęp do staging.",
    detail: "2-4 tyg.",
  },
  {
    icon: TestTube,
    number: "04",
    title: "Test + iteracje",
    desc: "Tydzień testów na produkcji + iteracje. Tu się sprawdza czy system rzeczywiście rozwiązuje Twój problem.",
    detail: "1 tydz.",
  },
  {
    icon: TrendingUp,
    number: "05",
    title: "Skalowanie",
    desc: "Po sukcesie pierwszego systemu - skalujemy. Voice + Lead gen + Support + Workflows = pełen stack AI.",
    detail: "Ongoing",
  }
];

export default function ProcessTimeline() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section aria-label="Process" className="py-16 md:py-24 bg-bg">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16">
        
        {/* Header */}
        <motion.div
          className="mb-16 md:mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-8 h-px bg-stroke"></div>
            <span className="text-xs text-muted uppercase tracking-[0.3em]">Jak pracujemy</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-light text-text-primary mb-4">
            Pięć kroków do <span className="font-display italic">działającego systemu</span>
          </h2>
          <p className="text-muted max-w-lg">
            Od pierwszej rozmowy do działającego systemu AI - transparentnie, z fixed-price wyceną i bez niespodzianek.
          </p>
        </motion.div>

        {/* Timeline */}
        <div ref={ref} className="relative">

          {/* Vertical connecting line */}
          <motion.div
            className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px md:-translate-x-px"
            style={{ background: 'linear-gradient(to bottom, transparent, hsl(var(--stroke)) 5%, hsl(var(--stroke)) 95%, transparent)' }}
            initial={{ scaleY: 0, transformOrigin: 'top' }}
            animate={isInView ? { scaleY: 1 } : { scaleY: 0 }}
            transition={{ duration: 1.8, ease: "easeOut", delay: 0.2 }}
          />

          <div className="space-y-8 md:space-y-0">
            {steps.map((step, idx) => {
              const isEven = idx % 2 === 0;

              return (
                <motion.div
                  key={idx}
                  className={`relative flex items-start md:items-center gap-8 md:gap-0 ${
                    idx !== steps.length - 1 ? 'pb-8 md:pb-0' : ''
                  }`}
                  initial={{ opacity: 0, y: 40 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
                  transition={{ duration: 0.7, delay: 0.25 * idx + 0.3, ease: "easeOut" }}
                >
                  {/* Left content (desktop) */}
                  <div className={`hidden md:flex w-1/2 ${isEven ? 'justify-end pr-12' : 'justify-end pr-12 order-first'}`}>
                    {isEven ? (
                      <TimelineCard step={step} align="right" />
                    ) : (
                      <TimelineTimestamp detail={step.detail} align="right" />
                    )}
                  </div>

                  {/* Center dot */}
                  <div className="relative z-10 flex-shrink-0 md:absolute md:left-1/2 md:-translate-x-1/2">
                    <motion.div
                      className="w-12 h-12 rounded-full border-2 border-stroke bg-bg flex items-center justify-center transition-colors group-hover:border-accent-from"
                      whileHover={{ scale: 1.1, borderColor: 'hsl(var(--accent-from))' }}
                    >
                      <span className="text-sm font-mono font-semibold text-text-primary">{step.number}</span>
                    </motion.div>
                  </div>

                  {/* Right content (desktop) */}
                  <div className={`hidden md:flex w-1/2 ${isEven ? 'justify-start pl-12' : 'justify-start pl-12'}`}>
                    {isEven ? (
                      <TimelineTimestamp detail={step.detail} align="left" />
                    ) : (
                      <TimelineCard step={step} align="left" />
                    )}
                  </div>

                  {/* Mobile content */}
                  <div className="md:hidden flex-grow min-w-0">
                    <TimelineCard step={step} align="left" />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

      </div>
    </section>
  );
}

/* ── Sub-components ── */

function TimelineCard({ step, align }: { step: typeof steps[number]; align: 'left' | 'right' }) {
  const Icon = step.icon;
  return (
    <div
      className={`group relative max-w-md w-full ${align === 'right' ? 'text-right' : 'text-left'}`}
    >
      <div className="bg-surface border border-stroke rounded-2xl p-6 transition-all duration-300 hover:border-accent-from/40 hover:shadow-lg hover:shadow-accent-from/5">
        {/* Icon + Title row */}
        <div className={`flex items-center gap-3 mb-3 ${align === 'right' ? 'justify-end flex-row-reverse' : ''}`}>
          <div className="w-10 h-10 rounded-xl bg-accent-from/10 flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5 text-accent-from" />
          </div>
          <h3 className="text-lg font-semibold text-text-primary">{step.title}</h3>
        </div>
        
        {/* Description */}
        <p className="text-sm text-muted leading-relaxed">{step.desc}</p>

        {/* Time badge - mobile */}
        <div className={`mt-4 md:hidden inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-bg border border-stroke text-xs text-muted ${align === 'right' ? 'float-right' : ''}`}>
          <span className="w-1.5 h-1.5 rounded-full bg-accent-from"></span>
          {step.detail}
        </div>
      </div>
    </div>
  );
}

function TimelineTimestamp({ detail, align }: { detail: string; align: 'left' | 'right' }) {
  return (
    <div className={`flex items-center h-full ${align === 'right' ? 'justify-end' : 'justify-start'}`}>
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-surface border border-stroke text-sm text-muted">
        <span className="w-2 h-2 rounded-full bg-accent-from animate-pulse"></span>
        {detail}
      </div>
    </div>
  );
}
